<template>
  <g ref="group"></g>
</template>

<script lang="ts" setup>
import * as d3 from 'd3'
import { ref, toRefs } from 'vue'
import { useComponent } from './useComponent'
import { ColorScaleConfig, getColorForValue } from '@/utils/colorScale'
import { Pattern } from '../chart/bars'

export type HighlightAreaProps = {
  path: [number, number][]
  value: [number, number]
  color: string
  colorScale?: ColorScaleConfig
  visible?: boolean
  label: string
  pattern?: Pattern
  strokeWidth?: number
  xLimits: [number, number]
  yLimits: [number, number]
}

const props = defineProps<HighlightAreaProps>()

const { value, path, label, xLimits, yLimits, strokeWidth, color, colorScale, pattern, visible } =
  toRefs(props)

const group = ref<SVGGElement | null>(null)

useComponent(group, ({ width, height, group }) => {
  const xScale = d3.scaleLinear().domain(xLimits?.value).range([0, width])
  const yScale = d3.scaleLinear().domain(yLimits?.value).range([0, height])

  const line = d3
    .line()
    .x((d) => xScale(d[0]))
    .y((d) => height - yScale(d[1]))

  const area = group
    .append('path')
    .attr('data-x', value.value?.[0])
    .attr('data-y', value.value?.[1])
    .attr('data-tooltip-x', path.value.reduce((a, b) => a + b[0], 0) / path.value.length)
    .attr('data-tooltip-y', path.value.reduce((a, b) => a + b[1], 0) / path.value.length)
    .attr('data-label', label.value)
    .attr('data-pattern', JSON.stringify(pattern?.value ? pattern.value : null))
    .attr(
      'data-color',
      (colorScale?.value
        ? getColorForValue(colorScale.value, value.value[1]).toHex()
        : color.value) ?? 'none'
    )
    .attr(
      'stroke',
      (visible?.value === false
        ? 'transparent'
        : colorScale?.value
        ? getColorForValue(colorScale.value, value.value[1]).toHex()
        : color.value) ?? 'none'
    )
    .attr('fill', 'white')
    .attr('opacity', '0')
    .attr('stroke-width', strokeWidth?.value ?? 2)
    .datum(path.value)
    .attr('d', line)

  area
    .on('mouseenter.highlight-area' + Math.random().toString(), () => {
      area.attr('opacity', '0.5').classed('highlight', true)
    })
    .on('mouseleave.highlight-area' + Math.random().toString(), () => {
      area.attr('opacity', '0').classed('highlight', false)
    })
})
</script>
